import { IEMRListForUserAndPetResponseData } from "@/api_framework/api_modals/EMRs";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useEMRDownloadAPI } from "@/app/hooks/api_hooks/emr/useEMRDownloadAPI";
import { useEMRdownloadPDF } from "@/app/hooks/api_hooks/emr/useEMRdownloadPDF";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { Menu, Transition } from "@headlessui/react";
import { TrashIcon } from "@heroicons/react/20/solid";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { FaPen, FaRegFilePdf } from "react-icons/fa6";
import React, { useEffect, useState } from "react";


import { Fragment } from "react/jsx-runtime";

interface IEMRMenu {
  customerId: string;
  petId: string;
  emr: IEMRListForUserAndPetResponseData;
  handleEMRQuickView: (emrId: string) => void;
  handleEditDrafEMR: (emr: any, cartId: string, isCompleted: boolean) => void;
  handleViewEMR: (emrId: string, emrCustomerId: string, emrPetId: string) => void;
  handleDeleteEMR: (emr: any, cartId: string) => void;
}

const EMRMenu: React.FC<IEMRMenu> = ({
  handleEMRQuickView,
  handleEditDrafEMR,
  handleViewEMR,
  handleDeleteEMR,
  emr,
  customerId,
  petId,
}) => {
  const downloadEMR = useEMRDownloadAPI();

  const userRole = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: EMR_MENU_ADMIN_ACTIONS — legacy role: super_admin
  // Bypasses EMR status/window/ownership restrictions on edit + delete.
  const canPerformEmrAdminActions = useFeatureAccess("EMR_MENU_ADMIN_ACTIONS");
  const { execute: generateEmrPDf } = useEMRdownloadPDF();
  const [isCompletedDeletionWindowActive, setIsCompletedDeletionWindowActive] = useState(false);

  useEffect(() => {
    if (canPerformEmrAdminActions || emr.emr_status !== "COMPLETED") {
      setIsCompletedDeletionWindowActive(false);
      return;
    }

    const deletionWindowMs = 30 * 60 * 1000;
    const createdAtMs = emr?.created_at ? (emr.created_at > 1e12 ? emr.created_at : emr.created_at * 1000) : 0;

    if (!createdAtMs) {
      setIsCompletedDeletionWindowActive(false);
      return;
    }

    const windowEndTime = createdAtMs + deletionWindowMs;
    const now = Date.now();

    if (now >= windowEndTime) {
      setIsCompletedDeletionWindowActive(false);
      return;
    }

    setIsCompletedDeletionWindowActive(true);

    const timeoutId = window.setTimeout(() => {
      setIsCompletedDeletionWindowActive(false);
    }, windowEndTime - now);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [emr.created_at, emr.emr_status, canPerformEmrAdminActions]);

  const canDeleteEmr =
    emr?.emr_type !== "Lab Report" &&
    (canPerformEmrAdminActions ||
      emr.emr_status === "DRAFT" ||
      (emr.emr_status === "COMPLETED" && isCompletedDeletionWindowActive));
  const shouldShowEditEmr = !["User Medicine Prescriptions", "Lab Report"].includes(emr?.emr_type);

  if (emr.emr_status !== "COMPLETED" && !shouldShowEditEmr && !canDeleteEmr) return null;

  return (
    <Menu as="div" className="relative flex-none mr-3 mt-4 md:mt-0">
      <Menu.Button className="block border-gray-100 bg-white shadow-md -m-2.5 p-2 border rounded-md text-gray-500 hover:text-gray-900">
        <span className="sr-only">Open options</span>
        <EllipsisVerticalIcon className="w-5 h-5" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="right-0 z-10 absolute space-y-1 bg-white shadow-lg mt-2 py-2 rounded-md ring-1 ring-gray-900/5 w-32 origin-top-right focus:outline-none">
          <RBACFlag actionId="EMR_DOWNLOAD_BUTTON">
            <>
              {/* <Menu.Item>
                {({ active }) => (
                  <a
                    href="#"
                    className={clsx(
                      active ? "bg-gray-50" : "",
                      " px-3 flex justify-start items-center py-1 text-sm gap-x-1 leading-6 text-gray-900",
                    )}
                    onClick={() => {
                      generateEmrPDf(emr.emr_id);
                    }}
                  >
                    <span className="text-md">
                      <FaRegEye />
                    </span>{" "}
                    <span className="text-xs">View EMR</span>
                  </a>
                )}
              </Menu.Item> */}

              {emr.emr_status === "COMPLETED" && (
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="#"
                      onClick={() => {
                        generateEmrPDf(emr.emr_id);
                      }}
                      className={clsx(
                        active ? "bg-gray-50" : "",
                        " px-3 flex justify-start cursor-pointer items-center py-1 text-sm gap-x-1 leading-6 text-gray-900",
                      )}
                    >
                      <span className="text-xs">
                        <FaRegFilePdf />
                      </span>{" "}
                      <span className="text-theme-xs">Download PDF</span>
                    </a>
                  )}
                </Menu.Item>
              )}
            </>
          </RBACFlag>

          {shouldShowEditEmr && (
            <RBACFlag actionId="EDIT_EMR_BUTTON">
              <Menu.Item>
                {({ active }) => (
                  <button
                    disabled={emr?.created_by === userRole || canPerformEmrAdminActions ? false : true}
                    onClick={() => {
                      handleEditDrafEMR(emr, emr?.cart_id, emr?.emr_status === "DRAFT" ? false : true);
                    }}
                    className={clsx(
                      active ? "bg-gray-50" : "",
                      `flex justify-start w-full  items-center ${emr?.created_by === userRole || canPerformEmrAdminActions ? "" : "bg-gray-300 cursor-not-allowed"} gap-x-1 px-3 py-1 text-sm leading-6 text-gray-900`,
                    )}
                  >
                    <span className="text-xs">
                      <FaPen />
                    </span>
                    <span className="text-theme-xs"> Edit</span>
                  </button>
                )}
              </Menu.Item>
            </RBACFlag>
          )}

          {canDeleteEmr && (
            <RBACFlag actionId="DELETE_EMR_BUTTON">
              <Menu.Item>
                {({ active }) => (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!canDeleteEmr) return;
                      handleDeleteEMR(emr, emr.cart_id);
                    }}
                    className={clsx(
                      active ? "bg-gray-50" : "",
                      "flex justify-start items-center gap-x-1 px-3 py-1 text-theme-xs leading-6 text-gray-900",
                    )}
                  >
                    <span className="text-xs text-red-600">
                      <TrashIcon className="w-4 h-4" />
                    </span>
                    <span className="text-red-600 text-theme-xs"> Delete</span>
                  </a>
                )}
              </Menu.Item>
            </RBACFlag>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default EMRMenu;
